/**
 * The two envelopes this backend answers in.
 *
 * Both are documented conventions across every journal and write endpoint, so
 * they are typed once here rather than re-described per service. Getting these
 * wrong is the single most common source of "it worked in Postman" bugs: a write
 * that failed still returns HTTP 200, and a page past the last page still returns
 * a page.
 */

/**
 * A paged listing.
 *
 * Beware the two meanings of "rows": the `rows` a caller SENDS is the page size,
 * the `rows` that comes BACK is the array of results. That collision is the
 * backend's own convention.
 */
export interface JournalResponse<T> {
  /** The page's results, in the requested order. Empty when nothing matched. */
  rows: T[];
  /**
   * The page actually returned — NOT necessarily the one asked for. A `pageNum`
   * past the last page returns the last page, so read this back into the pager
   * instead of assuming it echoes the request.
   */
  currPage: number;
  /** Total matches, ignoring paging. Page count is `ceil(rsCount / rows)`. */
  rsCount: number;
  /** True on the last page. Prefer this over probing for an empty page. */
  lastBlock: boolean;
  /**
   * True when `rsCount` saturated its cap and means "at least that many". A
   * capped count cannot produce a page count — `lastBlock` stays authoritative.
   */
  rsCountCapped?: boolean;
}

/**
 * A write result.
 *
 * `successfull` (the backend's spelling) is the only thing that says whether the
 * write happened. A rejected write — a duplicate code, a validation failure — is
 * still HTTP 200 with `successfull: false`, so branching on the status code
 * silently treats failures as successes.
 */
export interface ServerResponse<T> {
  successfull: boolean;
  payload: T | null;
  errorMessage: string | null;
}

/**
 * One rejected field.
 *
 * When a write fails validation the envelope carries an ARRAY of these as its
 * `payload` — in place of the object a success would have returned. So `payload` is
 * two different things depending on `successfull`, and the only safe way to read it
 * is to branch on that flag first.
 *
 * `path` names the field, which is what makes these worth more than one banner: a
 * message attached to the input that caused it is read, a list above the form is
 * scanned past. Every one of them is nullable, so nothing here can be assumed
 * present.
 */
export interface DataError {
  /** The rejected field, e.g. `email`. May be dotted, e.g. `userCard.email`. */
  path: string | null;
  message: string | null;
  errorCode: string | null;
}

/**
 * Whether a failed write's payload is a list of field errors.
 *
 * A failure can also carry no payload at all — a rejection that is not about a
 * particular field, such as a username already being taken — in which case
 * `errorMessage` is the whole story.
 */
export const isDataErrors = (payload: unknown): payload is DataError[] =>
  Array.isArray(payload) &&
  payload.every((entry) => entry !== null && typeof entry === 'object');

/** Thrown for anything that is not a 2xx, carrying what the server said. */
export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }

  /** Retention removed the row, or the id never existed. */
  get isNotFound(): boolean {
    return this.status === 404;
  }

  /** The caller's role does not permit this, and re-authenticating will not help. */
  get isForbidden(): boolean {
    return this.status === 403;
  }
}
